import { supabase } from './supabaseClient';
import { WebsiteMessage, WebsiteMessageService } from './websiteMessageService';
import { MessageCacheService } from './messageCache';
import { SurveyMessageService } from './surveyMessageService';
import {
  ExtendedWebsiteMessage,
  MessageFilters,
  MessageStats,
  BulkAction
} from '../types/messages';

// Project release message interface
interface ProjectReleaseMessage {
  id: string;
  project_id: string;
  message_id: string;
  recipient_user_id: string;
  sent_at: string;
  read_at?: string;
  clicked_at?: string;
  dismissed_at?: string;
}

// Task message interface
interface TaskMessage {
  id: string;
  task_id: string;
  message_id: string;
  recipient_user_id: string;
  submission_id: string;
  message_type: 'task_pending_review' | 'task_approved' | 'task_rejected';
  sent_at: string;
  read_at?: string;
  clicked_at?: string;
  dismissed_at?: string;
  metadata?: any;
}

// Survey message interface
interface SurveyMessage {
  id: string;
  questionnaire_id: string;
  message_id: string;
  recipient_user_id: string;
  submission_id: string;
  message_type: 'survey_completed';
  sent_at: string;
  read_at?: string;
  clicked_at?: string;
  dismissed_at?: string;
  metadata?: any;
}

/**
 * Unified service for managing all types of messages with enhanced functionality
 * Requirements: 1.1, 1.2, 2.1, 9.1, 9.2, 10.1, 10.3
 */
export class MessagesManagementService {
  private static subscriptions: { [key: string]: any } = {};

  /**
   * Get all messages for a user with unified aggregation and caching
   * Requirements: 1.1, 1.2, 2.1 - Performance optimizations
   */
  static async getAllUserMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Check cache first
      const cachedMessages = MessageCacheService.getCachedUserMessages(userId);
      if (cachedMessages && !MessageCacheService.needsRefresh()) {
        return cachedMessages;
      }

      // Fetch all message types in parallel for better performance
      const [
        globalMessages,
        projectMessages,
        taskMessages,
        surveyMessages,
        teamMessages,
        contactMessages
      ] = await Promise.all([
        this.getGlobalMessages(userId),
        this.getProjectReleaseMessages(userId),
        this.getTaskRelatedMessages(userId),
        this.getSurveyRelatedMessages(userId),
        this.getTeamMessages(userId),
        this.getContactMessages(userId)
      ]);

      // Combine and sort by creation date
      const allMessages = [...globalMessages, ...projectMessages, ...taskMessages, ...surveyMessages, ...teamMessages, ...contactMessages];
      allMessages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      // Cache the results
      MessageCacheService.cacheUserMessages(userId, allMessages);

      // Warm up cache with common filters
      MessageCacheService.warmUpCache(userId, allMessages);

      return allMessages;
    } catch (error) {
      console.error('Error getting all user messages:', error);
      throw new Error(`Failed to fetch user messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get global website messages with engagement data (excludes project_release and task messages)
   * Requirements: 2.1, 9.1
   */
  private static async getGlobalMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get all website messages except project_release, task-specific, survey-specific, and team-specific types
      const { data: messages, error } = await supabase
        .from('website_messages')
        .select('*')
        .eq('is_active', true)
        .not('message_type', 'in', '(project_release,task_pending_review,task_approved,task_rejected,survey_completed,team_setup,team_application_received,team_application_approved,team_application_rejected,team_formed,contact_form,contact_reply)') // Exclude project, task, survey, team-specific, and contact messages
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!messages || messages.length === 0) {
        return [];
      }

      // Get dismissed message IDs from localStorage for this user
      const dismissedMessageIds = WebsiteMessageService.getDismissedMessageIds(userId);

      // Get bookmarked message IDs from localStorage for this user
      const bookmarkedMessageIds = this.getBookmarkedMessageIds(userId);

      // Transform to extended messages
      return messages.map(message => this.transformToExtendedMessage(
        message,
        userId,
        'global',
        { views: 0, clicks: 0, dismissals: 0 },
        dismissedMessageIds.includes(message.id),
        bookmarkedMessageIds.includes(message.id)
      ));
    } catch (error) {
      console.error('Error getting global messages:', error);
      throw error; // Re-throw to be handled by getAllUserMessages
    }
  }

  /**
   * Get task-related messages with engagement data
   * Requirements: 2.1, 9.1
   */
  private static async getTaskRelatedMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get task-related messages for the user using the view
      const { data: taskMessages, error } = await supabase
        .from('user_task_messages')
        .select('*')
        .eq('recipient_user_id', userId)
        .eq('is_active', true)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      if (!taskMessages || taskMessages.length === 0) {
        return [];
      }

      // Get bookmarked message IDs from localStorage for this user
      const bookmarkedMessageIds = this.getBookmarkedMessageIds(userId);

      // Transform to extended messages
      return taskMessages.map(taskMessage => {
        // Create a WebsiteMessage-like object from the task message data
        const websiteMessage = {
          id: taskMessage.message_id,
          message_text: taskMessage.message_text,
          is_active: taskMessage.is_active,
          message_type: taskMessage.message_type,
          background_color: taskMessage.background_color,
          text_color: taskMessage.text_color,
          start_date: taskMessage.start_date,
          end_date: null,
          created_at: taskMessage.message_created_at,
          updated_at: taskMessage.message_created_at
        };

        return this.transformToExtendedMessage(
          websiteMessage,
          userId,
          'task_related',
          { views: 0, clicks: 0, dismissals: 0 }, // Engagement tracked in task_messages table
          !!taskMessage.dismissed_at,
          bookmarkedMessageIds.includes(taskMessage.message_id),
          {
            taskId: taskMessage.task_id,
            taskTitle: taskMessage.task_title,
            viewedAt: taskMessage.read_at,
            clickedAt: taskMessage.clicked_at,
            dismissedAt: taskMessage.dismissed_at
          }
        );
      });
    } catch (error) {
      console.error('Error getting task-related messages:', error);
      throw error; // Re-throw to be handled by getAllUserMessages
    }
  }

  /**
   * Get survey-related messages with engagement data
   * Requirements: 2.1, 9.1
   */
  private static async getSurveyRelatedMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get survey-related messages for the user using the view
      const { data: surveyMessages, error } = await supabase
        .from('user_survey_messages')
        .select('*')
        .eq('recipient_user_id', userId)
        .eq('is_active', true)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      if (!surveyMessages || surveyMessages.length === 0) {
        return [];
      }

      // Get bookmarked message IDs from localStorage for this user
      const bookmarkedMessageIds = this.getBookmarkedMessageIds(userId);

      // Transform to extended messages
      return surveyMessages.map(surveyMessage => {
        // Create a WebsiteMessage-like object from the survey message data
        const websiteMessage = {
          id: surveyMessage.message_id,
          message_text: surveyMessage.message_text,
          is_active: surveyMessage.is_active,
          message_type: surveyMessage.message_type,
          background_color: surveyMessage.background_color,
          text_color: surveyMessage.text_color,
          start_date: surveyMessage.start_date,
          end_date: null,
          created_at: surveyMessage.message_created_at,
          updated_at: surveyMessage.message_created_at
        };

        return this.transformToExtendedMessage(
          websiteMessage,
          userId,
          'survey_related',
          { views: 0, clicks: 0, dismissals: 0 }, // Engagement tracked in survey_messages table
          !!surveyMessage.dismissed_at,
          bookmarkedMessageIds.includes(surveyMessage.message_id),
          {
            questionnaireId: surveyMessage.questionnaire_id,
            questionnaireTitle: surveyMessage.questionnaire_title,
            viewedAt: surveyMessage.read_at,
            clickedAt: surveyMessage.clicked_at,
            dismissedAt: surveyMessage.dismissed_at
          }
        );
      });
    } catch (error) {
      console.error('Error getting survey-related messages:', error);
      throw error; // Re-throw to be handled by getAllUserMessages
    }
  }

  /**
   * Get team-related messages for a user
   * Requirements: 2.1, 9.1
   */
  private static async getTeamMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get team messages for the user
      const { data: teamMessages, error } = await supabase
        .from('team_messages')
        .select(`
          *,
          message:website_messages!inner(*)
        `)
        .eq('recipient_user_id', userId)
        .eq('message.is_active', true)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching team messages:', error);
        return [];
      }

      if (!teamMessages || teamMessages.length === 0) {
        return [];
      }

      // Get bookmarked message IDs from localStorage for this user
      const bookmarkedMessageIds = this.getBookmarkedMessageIds(userId);

      // Transform to extended messages
      return teamMessages.map(teamMessage => {
        const message = teamMessage.message;
        if (!message) return null;

        // Create a WebsiteMessage-like object
        const websiteMessage = {
          id: message.id,
          message_text: message.message_text,
          is_active: message.is_active,
          message_type: message.message_type,
          background_color: message.background_color,
          text_color: message.text_color,
          start_date: message.start_date,
          end_date: message.end_date,
          created_at: message.created_at,
          updated_at: message.updated_at
        };

        return this.transformToExtendedMessage(
          websiteMessage,
          userId,
          'team_related',
          { views: 0, clicks: 0, dismissals: 0 }, // Engagement tracked in team_messages table
          !!teamMessage.dismissed_at,
          bookmarkedMessageIds.includes(teamMessage.message_id),
          {
            teamId: teamMessage.team_id,
            teamRequestId: teamMessage.team_request_id,
            applicationId: teamMessage.application_id,
            viewedAt: teamMessage.read_at,
            clickedAt: teamMessage.clicked_at,
            dismissedAt: teamMessage.dismissed_at,
            metadata: teamMessage.metadata
          }
        );
      }).filter(Boolean) as ExtendedWebsiteMessage[];

    } catch (error) {
      console.error('Error in getTeamMessages:', error);
      return [];
    }
  }

  /**
   * Get contact messages for a user (only recipients see these)
   * Requirements: 2.1, 9.1
   */
  private static async getContactMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get contact messages for the user
      const { data: contactMessages, error } = await supabase
        .from('contact_messages')
        .select(`
          *,
          message:website_messages!contact_messages_message_id_fkey!inner(*)
        `)
        .eq('recipient_user_id', userId)
        .eq('message.is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching contact messages:', error);
        return [];
      }

      if (!contactMessages || contactMessages.length === 0) {
        return [];
      }

      // Get bookmarked message IDs from localStorage for this user
      const bookmarkedMessageIds = this.getBookmarkedMessageIds(userId);

      // Transform to extended messages
      return contactMessages.map(contactMessage => {
        const message = contactMessage.message;
        if (!message) return null;

        // Create a WebsiteMessage-like object
        const websiteMessage = {
          id: message.id,
          message_text: message.message_text,
          is_active: message.is_active,
          message_type: message.message_type,
          background_color: message.background_color,
          text_color: message.text_color,
          start_date: message.start_date,
          end_date: message.end_date,
          created_at: message.created_at,
          updated_at: message.updated_at
        };

        return this.transformToExtendedMessage(
          websiteMessage,
          userId,
          'global', // Contact messages appear as global messages in the UI
          { views: 0, clicks: 0, dismissals: 0 },
          contactMessage.is_read, // Mark as dismissed if already read
          bookmarkedMessageIds.includes(contactMessage.message_id),
          {
            viewedAt: contactMessage.is_read ? new Date().toISOString() : undefined,
            metadata: {
              contactMessageId: contactMessage.id,
              senderEmail: contactMessage.sender_email,
              senderName: contactMessage.sender_name,
              subjectCategory: contactMessage.subject_category,
              isRead: contactMessage.is_read,
              repliedAt: contactMessage.replied_at
            }
          }
        );
      }).filter(Boolean) as ExtendedWebsiteMessage[];

    } catch (error) {
      console.error('Error in getContactMessages:', error);
      return [];
    }
  }

  /**
   * Get project release messages with engagement data
   * Requirements: 2.1, 9.1
   */
  private static async getProjectReleaseMessages(userId: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Get project release messages for the user
      const { data: releaseMessages, error } = await supabase
        .from('project_release_messages')
        .select(`
          *,
          website_messages!inner (*),
          pre_release_projects (id, title)
        `)
        .eq('recipient_user_id', userId)
        .eq('website_messages.is_active', true)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      if (!releaseMessages || releaseMessages.length === 0) {
        return [];
      }

      // Transform to extended messages
      return releaseMessages.map(releaseMessage => {
        const message = releaseMessage.website_messages;
        const project = releaseMessage.pre_release_projects;

        return this.transformToExtendedMessage(
          message,
          userId,
          'project_release',
          { views: 0, clicks: 0, dismissals: 0 }, // Engagement tracked differently for project messages
          !!releaseMessage.dismissed_at,
          false, // Bookmarking handled separately for project messages
          {
            projectId: project?.id,
            projectTitle: project?.title,
            viewedAt: releaseMessage.read_at,
            clickedAt: releaseMessage.clicked_at,
            dismissedAt: releaseMessage.dismissed_at
          }
        );
      });
    } catch (error) {
      console.error('Error getting project release messages:', error);
      throw error; // Re-throw to be handled by getAllUserMessages
    }
  }

  /**
   * Transform a message to extended format
   * Requirements: 2.1
   */
  private static transformToExtendedMessage(
    message: WebsiteMessage,
    userId: string,
    source: 'global' | 'project_release' | 'task_related' | 'survey_related' | 'team_related',
    engagement: { views: number; clicks: number; dismissals: number },
    isDismissed: boolean = false,
    isBookmarked: boolean = false,
    projectData?: {
      projectId?: string;
      projectTitle?: string;
      viewedAt?: string;
      clickedAt?: string;
      dismissedAt?: string;
      taskId?: string;
      taskTitle?: string;
      questionnaireId?: string;
      questionnaireTitle?: string;
      teamId?: string;
      teamRequestId?: string;
      applicationId?: string;
      metadata?: any;
    }
  ): ExtendedWebsiteMessage {
    const now = new Date();
    const createdAt = new Date(message.created_at);
    const timeDiff = now.getTime() - createdAt.getTime();

    // Calculate time ago
    const timeAgo = this.calculateTimeAgo(timeDiff);

    // Check if message is currently active
    const isCurrentlyActive = this.isMessageActive(message);

    return {
      ...message,
      isDismissed,
      dismissedAt: projectData?.dismissedAt,
      isBookmarked,
      viewedAt: projectData?.viewedAt,
      clickedAt: projectData?.clickedAt,
      engagementCount: {
        views: engagement.views,
        clicks: engagement.clicks
      },
      messageSource: source,
      projectId: projectData?.projectId,
      projectTitle: projectData?.projectTitle,
      taskId: projectData?.taskId,
      taskTitle: projectData?.taskTitle,
      questionnaireId: projectData?.questionnaireId,
      questionnaireTitle: projectData?.questionnaireTitle,
      displayTimestamp: createdAt.toLocaleString(),
      timeAgo,
      canUndismiss: isDismissed && isCurrentlyActive,
      isActive: isCurrentlyActive // Computed property
    };
  }

  /**
   * Check if a message is currently active
   * Requirements: 2.1
   */
  private static isMessageActive(message: WebsiteMessage): boolean {
    if (!message.is_active) return false;

    const now = new Date().toISOString();
    const startDate = message.start_date;
    const endDate = message.end_date;

    return startDate <= now && (!endDate || endDate >= now);
  }

  /**
   * Calculate human-readable time ago
   * Requirements: 2.1
   */
  private static calculateTimeAgo(timeDiff: number): string {
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
    } else if (hours < 24) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else if (days < 30) {
      return days === 1 ? '1 day ago' : `${days} days ago`;
    } else {
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
  }

  /**
   * Search messages by content with highlighting support and caching
   * Requirements: 5.1, 5.2, 5.3, 5.4 - Performance optimizations
   */
  static async searchMessages(userId: string, query: string): Promise<ExtendedWebsiteMessage[]> {
    try {
      if (!query.trim()) {
        return await this.getAllUserMessages(userId);
      }

      // Check cache for search results
      const cachedResults = MessageCacheService.getCachedSearchResults(query);
      if (cachedResults) {
        const allMessages = await this.getAllUserMessages(userId);
        const cachedMessages = cachedResults
          .map(id => allMessages.find(m => m.id === id))
          .filter(Boolean) as ExtendedWebsiteMessage[];

        if (cachedMessages.length === cachedResults.length) {
          return cachedMessages;
        }
      }

      // Save search query to history
      this.saveSearchQuery(query);

      const allMessages = await this.getAllUserMessages(userId);
      const searchTerm = query.toLowerCase().trim();

      const filteredMessages = allMessages.filter(message =>
        message.message_text.toLowerCase().includes(searchTerm) ||
        message.message_type.toLowerCase().includes(searchTerm) ||
        (message.projectTitle && message.projectTitle.toLowerCase().includes(searchTerm)) ||
        (message.taskTitle && message.taskTitle.toLowerCase().includes(searchTerm)) ||
        (message.questionnaireTitle && message.questionnaireTitle.toLowerCase().includes(searchTerm))
      );

      // Cache search results
      MessageCacheService.cacheSearchResults(query, filteredMessages.map(m => m.id));

      return filteredMessages;
    } catch (error) {
      console.error('Error searching messages:', error);
      throw new Error(`Failed to search messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get search suggestions based on message content and history
   * Requirements: 5.4
   */
  static getSearchSuggestions(query: string): string[] {
    try {
      const suggestions: string[] = [];
      const searchHistory = this.getSearchHistory();

      if (!query.trim()) {
        // Return recent search history if no query
        return searchHistory.slice(0, 5);
      }

      const queryLower = query.toLowerCase();

      // Add matching search history
      const matchingHistory = searchHistory.filter(term =>
        term.toLowerCase().includes(queryLower)
      ).slice(0, 3);
      suggestions.push(...matchingHistory);

      // Add common search terms that match
      const commonTerms = [
        'announcement', 'maintenance', 'welcome', 'info', 'warning',
        'project', 'release', 'update', 'new', 'important',
        'task', 'pending', 'approved', 'rejected', 'submission',
        'survey', 'questionnaire', 'completed', 'research', 'team'
      ];

      const matchingCommon = commonTerms.filter(term =>
        term.toLowerCase().includes(queryLower) &&
        !suggestions.includes(term)
      ).slice(0, 2);
      suggestions.push(...matchingCommon);

      return suggestions.slice(0, 5);
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }

  /**
   * Save search query to history
   * Requirements: 5.4
   */
  private static saveSearchQuery(query: string): void {
    try {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || trimmedQuery.length < 2) return;

      const history = this.getSearchHistory();

      // Remove if already exists to avoid duplicates
      const filteredHistory = history.filter(term => term !== trimmedQuery);

      // Add to beginning and limit to 20 items
      const updatedHistory = [trimmedQuery, ...filteredHistory].slice(0, 20);

      localStorage.setItem('message_search_history', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving search query:', error);
    }
  }

  /**
   * Get search history from localStorage
   * Requirements: 5.4
   */
  static getSearchHistory(): string[] {
    try {
      const history = localStorage.getItem('message_search_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error getting search history:', error);
      return [];
    }
  }

  /**
   * Clear search history
   * Requirements: 5.4
   */
  static clearSearchHistory(): void {
    try {
      localStorage.removeItem('message_search_history');
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }

  /**
   * Highlight search terms in text
   * Requirements: 5.3
   */
  static highlightSearchTerms(text: string, searchQuery: string): string {
    if (!searchQuery.trim()) return text;

    const query = searchQuery.trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  /**
   * Filter messages based on criteria with caching
   * Requirements: 6.1, 6.2, 6.3, 6.4 - Performance optimizations
   */
  static async filterMessages(messages: ExtendedWebsiteMessage[], filters: MessageFilters): Promise<ExtendedWebsiteMessage[]> {
    try {
      // Check cache for filter results
      const cachedResults = MessageCacheService.getCachedFilterResults(filters);
      if (cachedResults) {
        const cachedMessages = cachedResults
          .map(id => messages.find(m => m.id === id))
          .filter(Boolean) as ExtendedWebsiteMessage[];

        if (cachedMessages.length === cachedResults.length) {
          return cachedMessages;
        }
      }

      let filteredMessages = [...messages];

      // Filter by read status
      if (filters.readStatus !== 'all') {
        switch (filters.readStatus) {
          case 'unread':
            filteredMessages = filteredMessages.filter(m => !m.viewedAt);
            break;
          case 'read':
            filteredMessages = filteredMessages.filter(m => m.viewedAt);
            break;
        }
      }

      // Filter by type
      if (filters.type !== 'all') {
        filteredMessages = filteredMessages.filter(m => m.message_type === filters.type);
      }

      // Filter by source
      if (filters.source !== 'all') {
        filteredMessages = filteredMessages.filter(m => m.messageSource === filters.source);
      }

      // Filter by date range
      if (filters.dateRange.start || filters.dateRange.end) {
        filteredMessages = filteredMessages.filter(m => {
          const messageDate = new Date(m.created_at);
          const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
          const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;

          if (startDate && messageDate < startDate) return false;
          if (endDate && messageDate > endDate) return false;
          return true;
        });
      }

      // Filter by bookmarked
      if (filters.bookmarked) {
        filteredMessages = filteredMessages.filter(m => m.isBookmarked);
      }

      // Cache filter results
      MessageCacheService.cacheFilterResults(filters, filteredMessages.map(m => m.id));

      return filteredMessages;
    } catch (error) {
      console.error('Error filtering messages:', error);
      throw new Error(`Failed to filter messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  /**
    * Calculate message statistics
    * Requirements: 6.1, 6.2
    */
  static calculateMessageStats(messages: ExtendedWebsiteMessage[]): MessageStats {
    const stats: MessageStats = {
      total: messages.length,
      unread: 0,
      read: 0,
      bookmarked: 0,
      byType: {},
      bySource: {}
    };

    messages.forEach(message => {
      // Count by read status
      if (message.viewedAt) {
        stats.read++;
      } else {
        stats.unread++;
      }

      // Count bookmarked
      if (message.isBookmarked) {
        stats.bookmarked++;
      }

      // Count by type
      stats.byType[message.message_type] = (stats.byType[message.message_type] || 0) + 1;

      // Count by source
      stats.bySource[message.messageSource] = (stats.bySource[message.messageSource] || 0) + 1;
    });

    return stats;
  }

  /**
   * Dismiss a single message
   * Requirements: 3.3, 4.1
   */
  static async dismissMessage(messageId: string, userId: string): Promise<void> {
    try {
      console.log('[dismissMessage] Starting dismiss for:', { messageId, userId });

      // Instead of relying on messageSource (which isn't in DB), 
      // check each table to see where this message exists
      const dismissTimestamp = new Date().toISOString();
      let dismissed = false;

      // Try team_messages first (most likely for new messages)
      console.log('[dismissMessage] Checking team_messages...');
      const { data: teamMessage, error: teamError } = await supabase
        .from('team_messages')
        .select('id')
        .eq('message_id', messageId)
        .eq('recipient_user_id', userId)
        .single();

      console.log('[dismissMessage] team_messages result:', {
        found: !!teamMessage,
        error: teamError?.message
      });

      if (teamMessage && !teamError) {
        console.log('[dismissMessage] Updating team_messages...');
        const { error: updateError } = await supabase
          .from('team_messages')
          .update({ dismissed_at: dismissTimestamp })
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId);

        if (updateError) {
          console.error('[dismissMessage] Error updating team_messages:', updateError);
        } else {
          console.log('[dismissMessage] Successfully updated team_messages');
          dismissed = true;
        }
      }

      // Try task_messages
      if (!dismissed) {
        const { data: taskMessage, error: taskError } = await supabase
          .from('task_messages')
          .select('id')
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId)
          .single();

        if (taskMessage && !taskError) {
          await supabase
            .from('task_messages')
            .update({ dismissed_at: dismissTimestamp })
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
          dismissed = true;
        }
      }

      // Try survey_messages
      if (!dismissed) {
        const { data: surveyMessage, error: surveyError } = await supabase
          .from('survey_messages')
          .select('id')
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId)
          .single();

        if (surveyMessage && !surveyError) {
          await supabase
            .from('survey_messages')
            .update({ dismissed_at: dismissTimestamp })
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
          dismissed = true;
        }
      }

      // Try project_release_messages
      if (!dismissed) {
        const { data: projectMessage, error: projectError } = await supabase
          .from('project_release_messages')
          .select('id')
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId)
          .single();

        if (projectMessage && !projectError) {
          await supabase
            .from('project_release_messages')
            .update({ dismissed_at: dismissTimestamp })
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
          dismissed = true;
        }
      }

      // Try contact_messages (for contact_form and contact_reply types)
      if (!dismissed) {
        console.log('[dismissMessage] Checking contact_messages...');
        const { data: contactMessage, error: contactError } = await supabase
          .from('contact_messages')
          .select('id')
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId)
          .single();

        console.log('[dismissMessage] contact_messages result:', {
          found: !!contactMessage,
          error: contactError?.message
        });

        if (contactMessage && !contactError) {
          console.log('[dismissMessage] Marking contact message as read...');
          const { error: updateError } = await supabase
            .from('contact_messages')
            .update({ is_read: true })
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);

          if (updateError) {
            console.error('[dismissMessage] Error updating contact_messages:', updateError);
          } else {
            console.log('[dismissMessage] Successfully updated contact_messages');
            dismissed = true;
          }
        }
      }

      // If not found in any specific table, it's a global message
      if (!dismissed) {
        console.log('[dismissMessage] Not found in any table, treating as global message');
        localStorage.setItem(`dismissed_message_${userId}_${messageId}`, dismissTimestamp);
        dismissed = true;
      }

      console.log('[dismissMessage] Dismiss completed, dismissed:', dismissed);

      // Invalidate the message cache for this user
      MessageCacheService.invalidateUserCache(userId);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('messageDismissed', {
        detail: { messageIds: [messageId], userId }
      }));

      console.log('[dismissMessage] All post-dismiss actions completed');
    } catch (error) {
      console.error('[dismissMessage] Error dismissing message:', error);
      throw new Error(`Failed to dismiss message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bulk dismiss messages
   * Requirements: 4.1, 4.2, 4.3
   */
  static async bulkDismissMessages(messageIds: string[], userId: string): Promise<void> {
    try {
      const allMessages = await this.getAllUserMessages(userId);
      const messagesToDismiss = allMessages.filter(m => messageIds.includes(m.id));

      for (const message of messagesToDismiss) {
        if (message.messageSource === 'global') {
          // Check if it's a contact message type
          if (message.message_type === 'contact_form' || message.message_type === 'contact_reply') {
            // For contact messages, mark as read in contact_messages table
            await supabase
              .from('contact_messages')
              .update({ is_read: true })
              .eq('message_id', message.id)
              .eq('recipient_user_id', userId);
          } else {
            // For other global messages, use user-specific localStorage key
            localStorage.setItem(`dismissed_message_${userId}_${message.id}`, new Date().toISOString());
          }
        } else if (message.messageSource === 'project_release') {
          // For project release messages, update database
          await supabase
            .from('project_release_messages')
            .update({ dismissed_at: new Date().toISOString() })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'task_related') {
          // For task messages, update task_messages table
          await supabase
            .from('task_messages')
            .update({ dismissed_at: new Date().toISOString() })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'survey_related') {
          // For survey messages, update survey_messages table
          await supabase
            .from('survey_messages')
            .update({ dismissed_at: new Date().toISOString() })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'team_related') {
          // For team messages, update team_messages table
          await supabase
            .from('team_messages')
            .update({ dismissed_at: new Date().toISOString() })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        }
      }

      // Invalidate the message cache for this user so fresh data is loaded
      MessageCacheService.invalidateUserCache(userId);

      // Dispatch custom event to notify other components (like top bar)
      window.dispatchEvent(new CustomEvent('messageDismissed', {
        detail: { messageIds, userId }
      }));
    } catch (error) {
      console.error('Error bulk dismissing messages:', error);
      throw new Error(`Failed to dismiss messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bulk undismiss messages
   * Requirements: 4.1, 4.2, 4.3
   */
  static async bulkUndismissMessages(messageIds: string[], userId: string): Promise<void> {
    try {
      const allMessages = await this.getAllUserMessages(userId);
      const messagesToUndismiss = allMessages.filter(m =>
        messageIds.includes(m.id) && m.isDismissed && m.canUndismiss
      );

      for (const message of messagesToUndismiss) {
        if (message.messageSource === 'global') {
          // For global messages, remove from user-specific localStorage
          localStorage.removeItem(`dismissed_message_${userId}_${message.id}`);
        } else if (message.messageSource === 'project_release') {
          // For project release messages, update database
          await supabase
            .from('project_release_messages')
            .update({ dismissed_at: null })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'task_related') {
          // For task messages, update task_messages table
          await supabase
            .from('task_messages')
            .update({ dismissed_at: null })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'survey_related') {
          // For survey messages, update survey_messages table
          await supabase
            .from('survey_messages')
            .update({ dismissed_at: null })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        } else if (message.messageSource === 'team_related') {
          // For team messages, update team_messages table
          await supabase
            .from('team_messages')
            .update({ dismissed_at: null })
            .eq('message_id', message.id)
            .eq('recipient_user_id', userId);
        }
      }

      // Invalidate the message cache for this user so fresh data is loaded
      MessageCacheService.invalidateUserCache(userId);

      // Dispatch custom event to notify other components (like top bar)
      if (messagesToUndismiss.length > 0) {
        window.dispatchEvent(new CustomEvent('messageDismissed', {
          detail: { messageIds: messagesToUndismiss.map(m => m.id), userId }
        }));
      }
    } catch (error) {
      console.error('Error bulk undismissing messages:', error);
      throw new Error(`Failed to undismiss messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Undismiss a single message
   * Requirements: 3.3, 3.4
   */
  static async undismissMessage(messageId: string, userId: string): Promise<void> {
    try {
      const allMessages = await this.getAllUserMessages(userId);
      const message = allMessages.find(m => m.id === messageId);

      if (!message || !message.isDismissed || !message.canUndismiss) {
        throw new Error('Message cannot be undismissed');
      }

      if (message.messageSource === 'global') {
        localStorage.removeItem(`dismissed_message_${userId}_${messageId}`);
      } else if (message.messageSource === 'project_release') {
        await supabase
          .from('project_release_messages')
          .update({ dismissed_at: null })
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId);
      } else if (message.messageSource === 'task_related') {
        await supabase
          .from('task_messages')
          .update({ dismissed_at: null })
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId);
      } else if (message.messageSource === 'survey_related') {
        await supabase
          .from('survey_messages')
          .update({ dismissed_at: null })
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId);
      } else if (message.messageSource === 'team_related') {
        await supabase
          .from('team_messages')
          .update({ dismissed_at: null })
          .eq('message_id', messageId)
          .eq('recipient_user_id', userId);
      }

      // Invalidate the message cache for this user so fresh data is loaded
      MessageCacheService.invalidateUserCache(userId);

      // Dispatch custom event to notify other components (like top bar)
      window.dispatchEvent(new CustomEvent('messageDismissed', {
        detail: { messageIds: [messageId], userId }
      }));
    } catch (error) {
      console.error('Error undismissing message:', error);
      throw new Error(`Failed to undismiss message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bookmark a message for a specific user
   * Requirements: 8.4
   */
  static async bookmarkMessage(messageId: string, userId: string): Promise<void> {
    try {
      const bookmarkedMessages = this.getBookmarkedMessageIds(userId);
      if (!bookmarkedMessages.includes(messageId)) {
        bookmarkedMessages.push(messageId);
      }
      localStorage.setItem(`bookmarked_messages_${userId}`, JSON.stringify(bookmarkedMessages));
    } catch (error) {
      console.error('Error bookmarking message:', error);
      throw new Error(`Failed to bookmark message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Remove bookmark from a message for a specific user
   * Requirements: 8.4
   */
  static async unbookmarkMessage(messageId: string, userId: string): Promise<void> {
    try {
      const bookmarkedMessages = this.getBookmarkedMessageIds(userId);
      const updatedBookmarks = bookmarkedMessages.filter(id => id !== messageId);
      localStorage.setItem(`bookmarked_messages_${userId}`, JSON.stringify(updatedBookmarks));
    } catch (error) {
      console.error('Error unbookmarking message:', error);
      throw new Error(`Failed to unbookmark message: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bulk bookmark messages for a specific user
   * Requirements: 4.1, 4.2, 4.3
   */
  static async bulkBookmarkMessages(messageIds: string[], userId: string): Promise<void> {
    try {
      const bookmarkedMessages = this.getBookmarkedMessageIds(userId);
      const newBookmarks = Array.from(new Set([...bookmarkedMessages, ...messageIds]));
      localStorage.setItem(`bookmarked_messages_${userId}`, JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error bulk bookmarking messages:', error);
      throw new Error(`Failed to bookmark messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bulk unbookmark messages for a specific user
   * Requirements: 4.1, 4.2, 4.3
   */
  static async bulkUnbookmarkMessages(messageIds: string[], userId: string): Promise<void> {
    try {
      const bookmarkedMessages = this.getBookmarkedMessageIds(userId);
      const updatedBookmarks = bookmarkedMessages.filter(id => !messageIds.includes(id));
      localStorage.setItem(`bookmarked_messages_${userId}`, JSON.stringify(updatedBookmarks));
    } catch (error) {
      console.error('Error bulk unbookmarking messages:', error);
      throw new Error(`Failed to unbookmark messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get bookmarked message IDs from localStorage for a specific user
   * Requirements: 8.4
   */
  private static getBookmarkedMessageIds(userId?: string): string[] {
    try {
      const key = userId ? `bookmarked_messages_${userId}` : 'bookmarked_messages';
      const bookmarked = localStorage.getItem(key);
      return bookmarked ? JSON.parse(bookmarked) : [];
    } catch (error) {
      console.error('Error getting bookmarked message IDs:', error);
      return [];
    }
  }

  /**
   * Subscribe to real-time message updates
   * Requirements: 9.4, 10.1
   */
  static subscribeToMessageUpdates(userId: string, callback: (message: ExtendedWebsiteMessage) => void): () => void {
    const subscriptionId = `messages_management_${userId}_${Date.now()}`;

    // Subscribe to website messages updates (global messages only, not team-specific)
    // Note: Team messages are handled by team_messages subscription below
    const websiteMessagesSubscription = supabase
      .channel('website_messages_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'website_messages',
          // Filter to only global messages (exclude team, task, survey, project-specific types)
          filter: `message_type=not.in.(team_application_received,team_application_approved,team_application_rejected,team_formed,task_pending_review,task_approved,task_rejected,survey_completed,project_release)`
        },
        async (payload) => {
          try {
            const message = payload.new as WebsiteMessage;
            if (message) {
              const extendedMessage = this.transformToExtendedMessage(
                message,
                userId,
                'global',
                { views: 0, clicks: 0, dismissals: 0 },
                false,
                false
              );
              callback(extendedMessage);
            }
          } catch (error) {
            console.error('Error processing website message update:', error);
          }
        }
      )
      .subscribe();

    // Subscribe to project release messages updates
    const projectMessagesSubscription = supabase
      .channel('project_release_messages_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_release_messages',
          filter: `recipient_user_id=eq.${userId}`
        },
        async (payload) => {
          try {
            // Fetch the complete message data when project release message changes
            const releaseMessage = payload.new as ProjectReleaseMessage;
            if (releaseMessage) {
              const { data: messageData } = await supabase
                .from('website_messages')
                .select('*')
                .eq('id', releaseMessage.message_id)
                .single();

              if (messageData) {
                const extendedMessage = this.transformToExtendedMessage(
                  messageData,
                  userId,
                  'project_release',
                  { views: 0, clicks: 0, dismissals: 0 },
                  !!releaseMessage.dismissed_at,
                  false,
                  {
                    projectId: releaseMessage.project_id,
                    viewedAt: releaseMessage.read_at,
                    clickedAt: releaseMessage.clicked_at,
                    dismissedAt: releaseMessage.dismissed_at
                  }
                );
                callback(extendedMessage);
              }
            }
          } catch (error) {
            console.error('Error processing project message update:', error);
          }
        }
      )
      .subscribe();

    // Subscribe to task-related messages updates
    const taskMessagesSubscription = supabase
      .channel(`task_messages_updates_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_messages',
          filter: `recipient_user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('[MessagesManagementService] Task message realtime event received:', {
            event: payload.eventType,
            taskMessageId: (payload.new as any)?.id,
            recipientUserId: (payload.new as any)?.recipient_user_id,
            messageType: (payload.new as any)?.message_type
          });

          try {
            const taskMessage = payload.new as TaskMessage;
            if (taskMessage && taskMessage.id) {
              console.log('[MessagesManagementService] Fetching complete task message data for:', taskMessage.id);

              // Fetch the complete message data from the view
              const { data: messageData, error: fetchError } = await supabase
                .from('user_task_messages')
                .select('*')
                .eq('id', taskMessage.id)
                .single();

              if (fetchError) {
                console.error('[MessagesManagementService] Error fetching task message from view:', fetchError);
                return;
              }

              if (messageData) {
                console.log('[MessagesManagementService] Task message data fetched successfully:', {
                  messageId: messageData.message_id,
                  taskTitle: messageData.task_title,
                  messageType: messageData.message_type
                });

                // Create a WebsiteMessage-like object from the task message data
                const websiteMessage = {
                  id: messageData.message_id,
                  message_text: messageData.message_text,
                  is_active: messageData.is_active,
                  message_type: messageData.message_type,
                  background_color: messageData.background_color,
                  text_color: messageData.text_color,
                  start_date: messageData.start_date,
                  end_date: null,
                  created_at: messageData.message_created_at,
                  updated_at: messageData.message_created_at
                };

                const extendedMessage = this.transformToExtendedMessage(
                  websiteMessage,
                  userId,
                  'task_related',
                  { views: 0, clicks: 0, dismissals: 0 },
                  !!messageData.dismissed_at,
                  false,
                  {
                    taskId: messageData.task_id,
                    taskTitle: messageData.task_title,
                    viewedAt: messageData.read_at,
                    clickedAt: messageData.clicked_at,
                    dismissedAt: messageData.dismissed_at
                  }
                );

                console.log('[MessagesManagementService] Calling callback with extended message');
                callback(extendedMessage);
              } else {
                console.warn('[MessagesManagementService] No message data found in view for task message:', taskMessage.id);
              }
            } else {
              console.warn('[MessagesManagementService] Invalid task message payload:', payload);
            }
          } catch (error) {
            console.error('[MessagesManagementService] Error processing task message update:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log('[MessagesManagementService] Task messages subscription status:', status);
      });

    // Subscribe to survey-related messages updates
    const surveyMessagesSubscription = supabase
      .channel(`survey_messages_updates_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'survey_messages',
          filter: `recipient_user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('[MessagesManagementService] Survey message realtime event received:', {
            event: payload.eventType,
            surveyMessageId: (payload.new as any)?.id,
            recipientUserId: (payload.new as any)?.recipient_user_id,
            messageType: (payload.new as any)?.message_type
          });

          try {
            const surveyMessage = payload.new as SurveyMessage;
            if (surveyMessage && surveyMessage.id) {
              console.log('[MessagesManagementService] Fetching complete survey message data for:', surveyMessage.id);

              // Fetch the complete message data from the view
              const { data: messageData, error: fetchError } = await supabase
                .from('user_survey_messages')
                .select('*')
                .eq('id', surveyMessage.id)
                .single();

              if (fetchError) {
                console.error('[MessagesManagementService] Error fetching survey message from view:', fetchError);
                return;
              }

              if (messageData) {
                console.log('[MessagesManagementService] Survey message data fetched successfully:', {
                  messageId: messageData.message_id,
                  questionnaireTitle: messageData.questionnaire_title,
                  messageType: messageData.message_type
                });

                // Create a WebsiteMessage-like object from the survey message data
                const websiteMessage = {
                  id: messageData.message_id,
                  message_text: messageData.message_text,
                  is_active: messageData.is_active,
                  message_type: messageData.message_type,
                  background_color: messageData.background_color,
                  text_color: messageData.text_color,
                  start_date: messageData.start_date,
                  end_date: null,
                  created_at: messageData.message_created_at,
                  updated_at: messageData.message_created_at
                };

                const extendedMessage = this.transformToExtendedMessage(
                  websiteMessage,
                  userId,
                  'survey_related',
                  { views: 0, clicks: 0, dismissals: 0 },
                  !!messageData.dismissed_at,
                  false,
                  {
                    questionnaireId: messageData.questionnaire_id,
                    questionnaireTitle: messageData.questionnaire_title,
                    viewedAt: messageData.read_at,
                    clickedAt: messageData.clicked_at,
                    dismissedAt: messageData.dismissed_at
                  }
                );

                console.log('[MessagesManagementService] Calling callback with extended message');
                callback(extendedMessage);
              } else {
                console.warn('[MessagesManagementService] No message data found in view for survey message:', surveyMessage.id);
              }
            } else {
              console.warn('[MessagesManagementService] Invalid survey message payload:', payload);
            }
          } catch (error) {
            console.error('[MessagesManagementService] Error processing survey message update:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log('[MessagesManagementService] Survey messages subscription status:', status);
      });

    // Subscribe to team-related messages updates
    const teamMessagesSubscription = supabase
      .channel('team_messages_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_messages',
          filter: `recipient_user_id=eq.${userId}`
        },
        async (payload) => {
          try {
            const teamMessage = payload.new as any;
            if (teamMessage && teamMessage.message_id) {
              // Fetch the complete message data
              const { data: messageData } = await supabase
                .from('website_messages')
                .select('*')
                .eq('id', teamMessage.message_id)
                .single();

              if (messageData) {
                const extendedMessage = this.transformToExtendedMessage(
                  messageData,
                  userId,
                  'team_related',
                  { views: 0, clicks: 0, dismissals: 0 },
                  !!teamMessage.dismissed_at,
                  false,
                  {
                    teamId: teamMessage.team_id,
                    teamRequestId: teamMessage.team_request_id,
                    applicationId: teamMessage.application_id,
                    viewedAt: teamMessage.read_at,
                    clickedAt: teamMessage.clicked_at,
                    dismissedAt: teamMessage.dismissed_at,
                    metadata: teamMessage.metadata
                  }
                );
                callback(extendedMessage);
              }
            }
          } catch (error) {
            console.error('Error processing team message update:', error);
          }
        }
      )
      .subscribe();

    // Store subscriptions
    this.subscriptions[subscriptionId] = {
      websiteMessages: websiteMessagesSubscription,
      projectMessages: projectMessagesSubscription,
      taskMessages: taskMessagesSubscription,
      surveyMessages: surveyMessagesSubscription,
      teamMessages: teamMessagesSubscription
    };

    // Return unsubscribe function
    return () => {
      if (this.subscriptions[subscriptionId]) {
        supabase.removeChannel(this.subscriptions[subscriptionId].websiteMessages);
        supabase.removeChannel(this.subscriptions[subscriptionId].projectMessages);
        supabase.removeChannel(this.subscriptions[subscriptionId].taskMessages);
        supabase.removeChannel(this.subscriptions[subscriptionId].surveyMessages);
        supabase.removeChannel(this.subscriptions[subscriptionId].teamMessages);
        delete this.subscriptions[subscriptionId];
      }
    };
  }

  /**
   * Get message by ID with full context
   * Requirements: 9.3
   */
  static async getMessageById(messageId: string, userId: string): Promise<ExtendedWebsiteMessage | null> {
    try {
      const allMessages = await this.getAllUserMessages(userId);
      return allMessages.find(m => m.id === messageId) || null;
    } catch (error) {
      console.error('Error getting message by ID:', error);
      return null;
    }
  }

  /**
   * Track message interaction (click, view, etc.)
   * Requirements: 8.3, 8.5
   */
  static async trackMessageInteraction(
    messageId: string,
    userId: string,
    interactionType: 'view' | 'click' | 'link_click' | 'action_button_click',
    metadata?: any
  ): Promise<void> {
    try {
      // For project release messages, update the specific tracking
      const message = await this.getMessageById(messageId, userId);
      if (message && message.messageSource === 'project_release') {
        const updateData: any = {};
        if (interactionType === 'view' && !message.viewedAt) {
          updateData.read_at = new Date().toISOString();
        } else if ((interactionType === 'click' || interactionType === 'link_click' || interactionType === 'action_button_click') && !message.clickedAt) {
          updateData.clicked_at = new Date().toISOString();
        }

        if (Object.keys(updateData).length > 0) {
          await supabase
            .from('project_release_messages')
            .update(updateData)
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
        }
      } else if (message && message.messageSource === 'task_related') {
        // For task messages, also update the specific tracking
        const updateData: any = {};
        if (interactionType === 'view' && !message.viewedAt) {
          updateData.read_at = new Date().toISOString();
        } else if ((interactionType === 'click' || interactionType === 'link_click' || interactionType === 'action_button_click') && !message.clickedAt) {
          updateData.clicked_at = new Date().toISOString();
        }

        if (Object.keys(updateData).length > 0) {
          await supabase
            .from('task_messages')
            .update(updateData)
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
        }
      } else if (message && message.messageSource === 'team_related') {
        // For team messages, viewing = dismissing (simplified approach)
        // We only track dismissed_at, not read_at separately
        const updateData: any = {};
        if (interactionType === 'view' && !message.isDismissed) {
          // When viewing, mark as dismissed (this is what affects badge count)
          updateData.dismissed_at = new Date().toISOString();
        } else if ((interactionType === 'click' || interactionType === 'link_click' || interactionType === 'action_button_click') && !message.clickedAt) {
          updateData.clicked_at = new Date().toISOString();
        }

        if (Object.keys(updateData).length > 0) {
          await supabase
            .from('team_messages')
            .update(updateData)
            .eq('message_id', messageId)
            .eq('recipient_user_id', userId);
        }
      } else if (message && (message.message_type === 'contact_form' || message.message_type === 'contact_reply')) {
        // For contact messages, update is_read field in contact_messages table
        if (interactionType === 'view' && message.metadata?.contactMessageId) {
          await supabase
            .from('contact_messages')
            .update({ is_read: true })
            .eq('id', message.metadata.contactMessageId)
            .eq('recipient_user_id', userId);

          // Dispatch event to update sidebar count
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('messageRead', {
              detail: { messageIds: [messageId] }
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error tracking message interaction:', error);
      // Don't throw - interaction tracking should not break user experience
    }
  }

  /**
   * Track link click in message content
   * Requirements: 8.2, 8.5
   */
  static async trackLinkClick(
    messageId: string,
    userId: string,
    url: string,
    isExternal: boolean
  ): Promise<void> {
    try {
      await this.trackMessageInteraction(messageId, userId, 'link_click', {
        url,
        link_type: isExternal ? 'external' : 'internal',
        source: 'messages_page',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking link click:', error);
    }
  }

  /**
   * Track action button click
   * Requirements: 8.3
   */
  static async trackActionButtonClick(
    messageId: string,
    userId: string,
    actionType: string,
    buttonText: string
  ): Promise<void> {
    try {
      await this.trackMessageInteraction(messageId, userId, 'action_button_click', {
        action_type: actionType,
        button_text: buttonText,
        source: 'messages_page',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking action button click:', error);
    }
  }

  /**
   * Clear all dismissed messages (admin function)
   * Requirements: 4.3
   */
  static clearAllDismissedMessages(): void {
    try {
      WebsiteMessageService.clearAllDismissedMessages();
    } catch (error) {
      console.error('Error clearing all dismissed messages:', error);
      throw new Error(`Failed to clear dismissed messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get active message count for notification badge (simplified from unread)
   * Requirements: 9.1, 9.2
   */
  static async getActiveMessageCount(userId: string): Promise<number> {
    try {
      // Force cache invalidation before counting
      MessageCacheService.invalidateUserCache(userId);

      const allMessages = await this.getAllUserMessages(userId);
      const activeMessages = allMessages.filter(m => !m.isDismissed);

      console.log('[getActiveMessageCount] Total messages:', allMessages.length);
      console.log('[getActiveMessageCount] Active messages:', activeMessages.length);
      console.log('[getActiveMessageCount] Team messages:', allMessages.filter(m => m.messageSource === 'team_related').map(m => ({
        id: m.id,
        type: m.message_type,
        isDismissed: m.isDismissed,
        dismissedAt: m.dismissedAt
      })));

      return activeMessages.length;
    } catch (error) {
      console.error('Error getting active message count:', error);
      return 0;
    }
  }

  /**
   * Bulk delete messages by setting is_active to false
   * Works for all message types
   * Requirements: 4.1, 4.2
   */
  static async bulkDeleteMessages(messageIds: string[], userId: string): Promise<void> {
    try {
      // Update is_active to false for all messages
      const { error } = await supabase
        .from('website_messages')
        .update({ is_active: false })
        .in('id', messageIds);

      if (error) {
        console.error('Error bulk deleting messages:', error);
        throw error;
      }

      // Invalidate cache
      MessageCacheService.invalidateUserCache(userId);

      console.log(`[bulkDeleteMessages] Successfully deleted ${messageIds.length} messages`);
    } catch (error) {
      console.error('Error bulk deleting messages:', error);
      throw error;
    }
  }
}
